import { useKnopClient } from "@opstel/contexts/knopClient/useKnopClient";
import { blobToBase64, IUserFileInteractions, UserFileInteractions } from "../utils/fileHelpers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useKnopIsLoading } from "./useKnopIsLoading";
import { useCurrentInstanceId } from "../contexts/instanceMetadata/useInstanceId";
import { OpenInstanceFromFileRequest } from "@opstel/gen/knop/ui/v1/api/instance_loading_pb";

/**
 * Custom hook to handle opening a KPP instance using TanStack Query mutation.
 */
export function useHandleOpenInstance(
  userFileInteractions: IUserFileInteractions = UserFileInteractions,
) {
  const { knopClient } = useKnopClient();
  const queryClient = useQueryClient();
  const { setCurrentInstanceId } = useCurrentInstanceId();
  const setLoading = useKnopIsLoading((state) => state.setLoading);
  const setReady = useKnopIsLoading((state) => state.setReady);
  return useMutation({
    mutationFn: async () => {
      const file = await userFileInteractions.selectFile(".kpp");
      if (!file) {
        return undefined;
      }
      const base64Data = await blobToBase64(file);
      setLoading();
      const response = await knopClient.openInstance(
        new OpenInstanceFromFileRequest({ base64Data }),
      );
      return {
        instanceId: response.instanceId,
        fileName: file.name,
      };
    },
    onError: (error) => {
      console.error("Unexpected error opening instance:", error);
    },
    onSuccess: (response) => {
      if (!response) {
        console.log("No file selected");
        return;
      }

      console.log("File opened successfully:", response.fileName);
      setCurrentInstanceId(response.instanceId);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries();
      setReady();
    },
  });
}

/**
 * Custom hook to handle downloading the current KPP instance using TanStack Query mutation.
 */
export function useHandleDownloadInstance(
  userFileInteractions: IUserFileInteractions = UserFileInteractions,
) {
  const { knopClient } = useKnopClient();
  const { instanceId } = useCurrentInstanceId();
  return useMutation({
    mutationFn: async () => {
      const result = await knopClient.downloadInstance({ instanceId: instanceId! });
      userFileInteractions.downloadFile(
        result.base64Data,
        result.fileName,
        "application/octet-stream",
      );
      return result.fileName;
    },
    onError: (error) => {
      console.error("Unexpected error downloading instance:", error);
    },
    onSuccess: (fileName) => {
      console.log("Downloaded instance successfully:", fileName);
    },
  });
}
